import {Alert, Box, Button, Chip, Drawer, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {StatusCallController} from '../../../api/status-calls/status-call.controller';
import {StatusCallStatusEnum} from '../../../model/status-calls/status-call-status.enum';
import {StatusCallModel} from '../../../model/status-calls/status-call.model';
import {FormatUtils} from '../../../utils/format.utils';
import {formatDuration} from '../utils/status-call.utils';

type Props = {
  call: StatusCallModel
  open: boolean
  onClose: () => void
  onChanged: () => Promise<void>
  translateLocal: (key: string, params?: Record<string, unknown>) => string
  translate: (key: string, params?: Record<string, unknown>) => string
};

export const StatusCallDrawer = ({call, open, onClose, onChanged, translateLocal, translate}: Props) => {
  const [editing, setEditing] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [title, setTitle] = useState(call.title ?? '');
  const [notes, setNotes] = useState(call.notes ?? '');
  const [meetingLink, setMeetingLink] = useState(call.meetingLink ?? '');
  const [participantInput, setParticipantInput] = useState('');
  const [participants, setParticipants] = useState<string[]>(call.participantEmails);
  const [date, setDate] = useState(call.scheduledAt.slice(0, 10));
  const [time, setTime] = useState(call.scheduledAt.slice(11, 16));
  const [durationMinutes, setDurationMinutes] = useState<30 | 60>(call.durationMinutes as 30 | 60);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(call.title ?? '');
    setNotes(call.notes ?? '');
    setMeetingLink(call.meetingLink ?? '');
    setParticipants(call.participantEmails);
    setDate(call.scheduledAt.slice(0, 10));
    setTime(call.scheduledAt.slice(11, 16));
    setDurationMinutes(call.durationMinutes as 30 | 60);
    setEditing(false);
    setRescheduling(false);
    setError(null);
  }, [call.id, call.version]);

  const isScheduled = call.status === ('SCHEDULED' as StatusCallStatusEnum);

  const handleAction = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setError(translateLocal('error.slot-conflict'));
      } else {
        setError(e?.message ?? 'Error');
      }
    }
  };

  const saveDetails = () => handleAction(async () => {
    await StatusCallController.updateStatusCallDetails(call.id, call.version, {
      title: title || null,
      notes: notes || null,
      meetingLink: meetingLink || null,
      participantEmails: participants,
    });
    setEditing(false);
  });

  const reschedule = () => handleAction(async () => {
    await StatusCallController.rescheduleStatusCall(call.id, call.version, {
      scheduledAt: `${date}T${time}:00`,
      durationMinutes,
    });
    setRescheduling(false);
  });

  const cancel = () => handleAction(() => StatusCallController.cancelStatusCall(call.id, call.version));
  const complete = () => handleAction(() => StatusCallController.completeStatusCall(call.id, call.version));

  const addParticipant = () => {
    const value = participantInput.trim();
    if (value && /\S+@\S+\.\S+/.test(value) && !participants.includes(value)) {
      setParticipants([...participants, value]);
      setParticipantInput('');
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{width: 480, p: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
        <Typography variant="h6">{translateLocal('drawer.title')}</Typography>
        <Box>
          <Typography variant="body2" color="text.secondary">{call.organizationName ?? '-'}</Typography>
          <Typography variant="body1">{FormatUtils.formatDate(call.scheduledAt)} ({formatDuration(call.durationMinutes)})</Typography>
          <Chip label={translate(`status-calls.status.${call.status}`)} size="small" sx={{mt: 1}}/>
        </Box>

        <Typography variant="subtitle2">{translateLocal('drawer.section.details')}</Typography>
        {editing ? (
          <>
            <TextField label={translateLocal('modal.schedule.title-field')} value={title} onChange={(e) => setTitle(e.target.value)}/>
            <TextField label={translateLocal('modal.schedule.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} multiline rows={3}/>
            <TextField label={translateLocal('modal.schedule.meeting-link')} value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)}/>
            <Box>
              <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                {participants.map(p => (
                  <Chip key={p} label={p} onDelete={() => setParticipants(participants.filter(x => x !== p))}/>
                ))}
              </Box>
              <TextField
                label={translateLocal('modal.schedule.participants')}
                value={participantInput}
                onChange={(e) => setParticipantInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addParticipant();
                  }
                }}
                onBlur={addParticipant}
                fullWidth
              />
            </Box>
            <Box display="flex" gap={1}>
              <Button variant="contained" onClick={saveDetails}>{translateLocal('drawer.button.save')}</Button>
              <Button onClick={() => setEditing(false)}>{translateLocal('modal.schedule.cancel')}</Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2"><b>{translateLocal('modal.schedule.title-field')}:</b> {call.title ?? '-'}</Typography>
            <Typography variant="body2"><b>{translateLocal('modal.schedule.notes')}:</b> {call.notes ?? '-'}</Typography>
            <Typography variant="body2"><b>{translateLocal('modal.schedule.meeting-link')}:</b> {call.meetingLink ?? '-'}</Typography>
            <Typography variant="subtitle2">{translateLocal('drawer.section.participants')}</Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {call.participantEmails.length === 0
                ? <Typography variant="body2" color="text.secondary">-</Typography>
                : call.participantEmails.map(p => <Chip key={p} label={p} size="small"/>)}
            </Box>
          </>
        )}

        {rescheduling && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={2}>
              <TextField
                label={translateLocal('modal.schedule.date')}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{shrink: true}}
                fullWidth
              />
              <TextField
                label={translateLocal('modal.schedule.time')}
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                InputLabelProps={{shrink: true}}
                inputProps={{step: 1800}}
                fullWidth
              />
            </Box>
            <FormControl>
              <FormLabel>{translateLocal('modal.schedule.duration')}</FormLabel>
              <RadioGroup
                row
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Number(e.target.value) as 30 | 60)}
              >
                <FormControlLabel value={30} control={<Radio/>} label={translateLocal('modal.schedule.duration30')}/>
                <FormControlLabel value={60} control={<Radio/>} label={translateLocal('modal.schedule.duration60')}/>
              </RadioGroup>
            </FormControl>
            <Box display="flex" gap={1}>
              <Button variant="contained" onClick={reschedule}>{translateLocal('drawer.button.save')}</Button>
              <Button onClick={() => setRescheduling(false)}>{translateLocal('modal.schedule.cancel')}</Button>
            </Box>
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!editing && !rescheduling && (
          <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
            <Button variant="outlined" onClick={() => setEditing(true)}>{translateLocal('drawer.button.edit')}</Button>
            {isScheduled && <>
              <Button variant="outlined" onClick={() => setRescheduling(true)}>{translateLocal('drawer.button.reschedule')}</Button>
              <Button variant="outlined" color="success" onClick={complete}>{translateLocal('drawer.button.complete')}</Button>
              <Button variant="outlined" color="error" onClick={cancel}>{translateLocal('drawer.button.cancel')}</Button>
            </>}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
